import React from 'react';

interface Aspect {
  name: string;
  weight: number;
  scaleGuidance: Record<string, string>;
}

interface ScoringGuidelinesProps {
  selectedCategory: string;
  guidelinesExpanded: boolean;
  onToggleGuidelines: () => void;
  loadingCriteria: boolean;
  aspects: Aspect[];
}

const ScoringGuidelines: React.FC<ScoringGuidelinesProps> = ({
  selectedCategory,
  guidelinesExpanded,
  onToggleGuidelines,
  loadingCriteria,
  aspects,
}) => {
  if (!selectedCategory) return null;

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
      {/* Header with Toggle Button */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <h3 className="text-lg font-baijamjuree font-medium text-gray-700">
          Scoring Guidelines - {selectedCategory}
        </h3>
        <div className="flex items-center gap-4">
          <button
            onClick={onToggleGuidelines}
            className="flex items-center gap-2 px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded border border-gray-300 transition-colors font-baijamjuree"
          >
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${guidelinesExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
            {guidelinesExpanded ? 'Hide Guidelines' : 'Show Guidelines'}
          </button>
        </div>
      </div>

      {/* Collapsible Content */}
      {guidelinesExpanded && (
        <div className="p-4">
          {loadingCriteria ? (
            <div className="p-4 bg-gray-50 rounded border border-gray-200">
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full"></div>
                <p className="text-gray-600 font-baijamjuree font-normal text-base">
                  Loading evaluation criteria for {selectedCategory}...
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {aspects.map((aspect, index) => {
                const cleanAspectName = aspect.name
                  .replace(/^.*?\s*-\s*/, '')
                  .replace(/\s+/g, ' ')
                  .trim();

                const g = aspect.scaleGuidance;

                return (
                  <div
                    key={index}
                    className="bg-white rounded border border-gray-200 p-4 min-h-[180px] flex flex-col"
                  >
                    <div className="mb-3 min-h-[60px] flex flex-col justify-start">
                      <h4 className="text-base font-semibold text-gray-800 font-baijamjuree mb-2 leading-tight min-h-[40px] flex items-start">
                        {cleanAspectName}
                      </h4>
                      <div className="text-sm text-gray-600 font-medium bg-gray-100 px-2 py-1 rounded inline-block self-start">
                        Weight: {aspect.weight}%
                      </div>
                    </div>

                    <div className="space-y-2 flex-1">
                      {/* High Score (8-10) */}
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-8 h-5 bg-green-500 rounded flex items-center justify-center text-xs font-medium text-white">
                          8-10
                        </div>
                        <div className="text-sm text-gray-700 leading-relaxed">
                          {g['10'] ||
                            'Outstanding performance with clear impact'}
                        </div>
                      </div>
                      {/* Medium Score (4-7) */}
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-8 h-5 bg-yellow-500 rounded flex items-center justify-center text-xs font-medium text-white">
                          4-7
                        </div>
                        <div className="text-sm text-gray-700 leading-relaxed">
                          {g['5'] ||
                            'Solid performance with room for improvement'}
                        </div>
                      </div>
                      {/* Low Score (0-3) */}
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-8 h-5 bg-red-500 rounded flex items-center justify-center text-xs font-medium text-white">
                          0-3
                        </div>
                        <div className="text-sm text-gray-700 leading-relaxed">
                          {g['0'] || 'Below expectations, requires development'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScoringGuidelines;
